import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getInsights } from "../services/insightsService";
import { useSelector } from "react-redux";
import { selectUserId } from "../store/slices/authUserSlice";

interface Insight {
  id: string;
  user_id: string;
  conversation_id: string
  title: string;
  summary: string;
  quotes: string[];
  created_at: string;
}

const Insights: React.FC = () => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const userId = useSelector(selectUserId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    setIsLoading(true);
    getInsights()
      .then((data) => {
        setInsights(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching insights:", error);
        setIsLoading(false);
      });
  }, [userId]);

  // Group insights by date (YYYY-MM-DD)
  const groupedInsights = insights.reduce((groups: Record<string, Insight[]>, insight) => {
    const date = new Date(insight.created_at);
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(insight);
    return groups;
  }, {});

  // Sort dates newest first
  const sortedDates = Object.keys(groupedInsights).sort((a, b) => {
    return new Date(b).getTime() - new Date(a).getTime();
  });

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleBeginInterview = () => {
    navigate('/interviewer');
  }

  return (
    <div>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: "24px"
      }}>
        <h1>Insights</h1>
        <button
          className="primary-button"
          onClick={() => handleBeginInterview()}
        >
          Start Interview
        </button>
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      ) : insights.length === 0 ? (
        <div className="empty-state-container">
          <h3>No insights found</h3>
          <p>Start an interview to generate insights.</p>
        </div>
      ) : (
        <div>
          {sortedDates.map((date) => {
            const [year, month, day] = date.split('-').map(num => parseInt(num, 10));
            const localDate = new Date(year, month - 1, day);
            return (
              <div key={date} className="date-group">
                <h3 className="date-heading">{localDate.toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</h3>
                <ul className="newsletter-list">
                  {groupedInsights[date].map((insight) => (
                    <li
                      key={insight.id}
                      className="newsletter-item"
                      onClick={() => navigate(`/insights/${insight.id}`)}>
                      <div className="newsletter-item-content-left">
                        <Link
                          to={`/insights/${insight.id}`}
                          className="newsletter-title"
                          style={{ textDecoration: 'none' }}
                        >
                          {insight.title}
                        </Link>
                      </div>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}>
                        <span className="list-tag-purple">
                          Quotes: {insight.quotes ? insight.quotes.length : 0}
                        </span>
                        <span className="list-tag-gray">
                          {new Date(insight.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <button
                          className="tertiary-info-button"
                          style={{
                            cursor: "pointer",
                            marginRight: "12px",
                          }}
                        >
                          View
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          )}
        </div>
      )}
    </div>
  );
};

export default Insights;
